import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ASSETS } from "@/constants/site";

export default function Login() {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (user) navigate("/admin", { replace: true });
    }, [user, navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email.trim().toLowerCase(), password);
            navigate("/admin", { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="login-page" className="min-h-screen grid lg:grid-cols-2">
            <div className="hidden lg:flex bg-[#0A192F] text-white p-16 flex-col justify-between relative overflow-hidden">
                <div className="absolute inset-0 bg-grain opacity-10" />
                <Link to="/" className="relative z-10 flex items-center gap-3">
                    <div className="bg-white p-2 rounded-sm">
                        <img src={ASSETS.logo} alt="HWC" className="h-10" />
                    </div>
                    <span className="font-serif text-xl font-bold">
                        Hopewell Consulting
                    </span>
                </Link>
                <div className="relative z-10">
                    <span className="overline">Client portal</span>
                    <h1 className="font-serif text-5xl font-bold mt-4 leading-tight">
                        Welcome back.
                    </h1>
                    <p className="mt-6 text-slate-300 max-w-md">
                        Manage services, blog posts, testimonials, impact
                        stats, settings, and incoming leads — all from one
                        clean dashboard.
                    </p>
                </div>
                <p className="relative z-10 text-xs text-slate-500">
                    © {new Date().getFullYear()} Hopewell Consulting LLC
                </p>
            </div>

            <div className="flex items-center justify-center p-8 lg:p-16 bg-white">
                <div className="w-full max-w-md">
                    <div className="lg:hidden flex items-center gap-3 mb-10">
                        <img src={ASSETS.logo} alt="HWC" className="h-10" />
                        <span className="font-serif text-lg font-bold text-[#0A192F]">
                            Hopewell Consulting
                        </span>
                    </div>
                    <span className="overline">Sign in</span>
                    <h2 className="font-serif text-3xl font-bold mt-3 text-[#0A192F]">
                        Owner Login
                    </h2>
                    <p className="mt-3 text-sm text-slate-500">
                        Authorized personnel only.
                    </p>

                    <form onSubmit={onSubmit} className="mt-10 space-y-6">
                        <div>
                            <label className="exec-input-label">Email</label>
                            <input
                                type="email"
                                required
                                data-testid="login-email-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="exec-input"
                                placeholder="you@hopewell.com"
                            />
                        </div>
                        <div>
                            <label className="exec-input-label">Password</label>
                            <input
                                type="password"
                                required
                                data-testid="login-password-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="exec-input"
                                placeholder="••••••••"
                            />
                        </div>
                        {error && (
                            <p
                                data-testid="login-error"
                                className="text-sm text-red-600"
                            >
                                {error}
                            </p>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit"
                            className="w-full inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white hover:bg-[#1E3A8A] disabled:opacity-60 rounded-sm px-6 py-4 font-bold text-sm uppercase tracking-widest"
                        >
                            {loading ? "Signing in…" : "Sign In"}
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </form>

                    <Link
                        to="/"
                        className="mt-8 block text-xs text-slate-500 hover:text-[#0A192F]"
                    >
                        ← Back to site
                    </Link>
                </div>
            </div>
        </div>
    );
}
